import React from 'react'
import {
  DemographicsSection,
  Divider,
  MetricCard,
  StatsCard,
  DescriptionAndContactInfo,
} from 'components/NewCompanyPage/Sidebar/shared'
import { CARNEGIE_CLASSIFICATION_SIZE_OPTIONS } from 'utils/carnegieClassificationSize'
import {
  formatAddress,
  formatInteger,
  integerFormatterOrFallback,
} from 'utils/formatting'
import { UniversityChainProxy } from 'models/universities'
import { GoogleMapsLink } from '../utils/GoogleMapsLink'
import { BuildCompanyTypeContentOptions, CompanyTypePageContent } from './types'

/**
 * Shared university content derivation used by both `NewUniversityPage`
 * (desktop) and `MobileUniversityPage`. See {@link CompanyTypePageContent}.
 */
export function buildUniversityPageContent(
  data: UniversityChainProxy | undefined,
  { onViewDoors }: BuildCompanyTypeContentOptions = {}
): CompanyTypePageContent {
  const university = data?.university

  const universityStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Dormitory Capacity',
      value: university?.dormitory_capacity
        ? formatInteger(university.dormitory_capacity)
        : 'N/A',
    },
    {
      label: 'Total Students Enrolled',
      value: university?.total_students_enrolled
        ? formatInteger(university.total_students_enrolled)
        : 'N/A',
    },
    {
      label: 'Meal Plan Provided',
      value: university?.meal_plan_provided ? 'Yes ✅' : 'No ⛔',
    },
    {
      label: 'Food Management Co.',
      value: university?.food_management_company || 'N/A',
    },
    {
      label: 'Parent Food Mgmt. Co.',
      value: university?.food_management_company_parent || 'N/A',
    },
    {
      label: 'Estimated Meals per Day',
      value: integerFormatterOrFallback(university?.estimated_meals_per_day),
    },
  ]

  const carnegieClassificationLabel =
    CARNEGIE_CLASSIFICATION_SIZE_OPTIONS.find(
      (option) =>
        option.value === university?.carnegie_classification_2021_size_setting
    )?.label ?? 'N/A'

  const studentStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Student Composition',
      value:
        university?.carnegie_classification_2021_size_setting_student_composition ||
        'N/A',
    },
    {
      label: 'Carnegie Classification: Year',
      value:
        university?.carnegie_classification_2021_size_setting_year || 'N/A',
    },
    {
      label: 'Carnegie Classification: Size',
      value: carnegieClassificationLabel,
    },
    {
      label: 'Carnegie Size Setting',
      value:
        university?.carnegie_classification_2021_size_setting_size || 'N/A',
    },
    {
      label: 'HBCU Status',
      value: university?.hbcu_status === 1 ? 'Yes' : 'No',
    },
    {
      label: 'Campus Count',
      value: university?.campus_count
        ? formatInteger(university.campus_count)
        : 'N/A',
    },
  ]

  const summaryContent: React.ReactNode[] = []

  summaryContent.push(
    <DescriptionAndContactInfo
      key="description-contact"
      address={
        formatAddress({
          street: university?.address,
          city: university?.city,
          state: university?.state_abbr,
          zip: university?.zipcode,
        }) ?? undefined
      }
      phone={data?.phone_number || undefined}
      googlePlaceUrl={university?.google_place_url}
    />
  )

  if (onViewDoors && data?.doors_count && data.doors_count > 0) {
    summaryContent.push(
      <MetricCard
        key="doors"
        value={data.doors_count}
        label="Door Count"
        actionText="View Doors"
        onActionClick={onViewDoors}
      />
    )
  }

  summaryContent.push(
    <StatsCard key="university-stats" stats={universityStats} />,
    <Divider key="divider-1" />,
    <StatsCard key="student-stats" stats={studentStats} />
  )

  const campusLocation = university?.address
    ? `${university.city}, ${university.state_abbr} - USA`
    : 'N/A'

  const campusInfoStats: Array<{ label: string; value: React.ReactNode }> = [
    {
      label: 'Full Address',
      value: <GoogleMapsLink address={university?.address} />,
    },
    {
      label: 'ZIP Code',
      value: university?.zipcode || 'N/A',
    },
    {
      label: 'State',
      value: university?.state_abbr || 'N/A',
    },
    {
      label: 'Tuition & Fees (2021-22)',
      value: university?.tuition_fees_2021_22
        ? `$${formatInteger(university.tuition_fees_2021_22)}`
        : 'N/A',
    },
  ]

  const locationDetailsContent: React.ReactNode[] = [
    <MetricCard
      key="campus-location"
      value={campusLocation}
      label="Campus Location"
      variant="full-width"
    />,
    <StatsCard key="campus-info" stats={campusInfoStats} />,
  ]

  if (university?.address_info) {
    locationDetailsContent.push(
      <Divider key="divider-2" />,
      <DemographicsSection
        key="zipcode-demographics"
        addressInfo={university.address_info}
      />
    )
  }

  return {
    documentTitle: `${university?.institution_name || 'Not Found'} (University) - First Bite`,
    header: {
      name: university?.institution_name ?? '',
      domain: university?.website_address ?? undefined,
      logoUrl: undefined,
    },
    contactCompanyId: data?.id,
    doorsCount: data?.doors_count ?? 0,
    summaryContent,
    locationDetailsContent,
    breadcrumbRoot: 'Education - University',
    breadcrumbTitle: 'University',
    categoryInfo: {
      categorySlug: 'non-commercial',
      subCategorySlug: 'education-cu',
    },
    companyTypeSlug: 'education-cu',
    tableTypeSlug: 'education-cu',
    modalSubCategory: {
      name: 'University',
      slug: 'education-cu',
      description: 'University',
    },
  }
}
